import cx from "classnames";

import EditIcon from "atoms/icon/icons/edit.svg";
import DeleteIcon from "atoms/icon/icons/trash.svg";
import WarningIcon from "atoms/icon/icons/warning_circle_info.svg";
import { Spinner } from "atoms/Spinner/Spinner";
import { Text } from "atoms/Text/Text";

const SMALL_IMAGE_THRESHOLD = 130;

type Props = {
  name?: string;
  value?: string;
  invalid?: boolean;
  error?: string;
  accept: string;
  isLoading?: boolean;
  imageHeight: number;
  imageWidth: number;
  resolutionHeight?: number;
  resolutionWidth?: number;
  onChange: (file?: File) => void;
  onDelete?: () => void;
  dataTestId?: string;
  disabled?: boolean;
};

export const ImageUploaderTemplate = ({
  name,
  value,
  invalid,
  error,
  accept,
  isLoading,
  onChange,
  imageHeight,
  imageWidth,
  resolutionHeight,
  resolutionWidth,
  onDelete,
  dataTestId,
  disabled,
}: Props) => {
  return (
    <div className="flex items-center" style={{ gap: "16px" }}>
      {value && (
        <div className="flex flex-col items-center gap-2">
          <div
            className="relative overflow-hidden rounded-lg"
            style={{
              height: 100,
              width: 167,
            }}
          >
            <img
              alt="preview"
              className="h-full w-full object-cover"
              data-testid={`${dataTestId}-preview`}
              src={value}
            />
          </div>
          {onDelete && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="flex items-center text-chip font-medium underline"
                onClick={onDelete}
                data-testid="remove-media-link"
              >
                <DeleteIcon className="h-4 w-4" />
                Remove Media
              </button>
            </div>
          )}
        </div>
      )}

      {!value && (
        <div
          className="group relative"
          style={{
            height: 100,
            width: 167,
            opacity: 1,
          }}
        >
          <input name={name} type="hidden" value={value} />
          <input
            accept={accept}
            aria-invalid={invalid || !!error}
            className={cx(
              "peer absolute inset-0 h-full w-full opacity-0 placeholder:!text-transparent",
              disabled ? "cursor-not-allowed" : "cursor-pointer"
            )}
            data-testid={dataTestId}
            disabled={isLoading || disabled}
            type="file"
            onChange={(event) => onChange(event.target.files?.[0])}
          />
          <div
            className={cx(
              "pointer-events-none absolute inset-0 rounded-lg border border-gray-300 bg-white peer-aria-invalid:border-red",
              !isLoading && !disabled && "group-hover:border-gray-400"
            )}
          >
            {!disabled && !value && !isLoading && (
              <div className="flex h-full w-full flex-col items-center justify-center text-center">
                {!error && (
                  <div className="flex flex-row items-center justify-center gap-2">
                    <Text variant="body2">+ Upload Media</Text>
                  </div>
                )}
              </div>
            )}
            {value && (
              <>
                <div className="absolute inset-0">
                  <img
                    alt="preview"
                    className={cx(
                      "h-full w-full rounded-lg bg-background object-scale-down",
                      (isLoading || error) && "opacity-10",
                      !disabled && "group-hover:opacity-10"
                    )}
                    data-testid={`${dataTestId}-preview`}
                    src={value}
                  />
                </div>

                {!isLoading && !error && !disabled && (
                  <div className="pointer-events-none">
                    <div className="absolute hidden h-full w-full items-center justify-center text-center group-hover:flex">
                      <div className="absolute p-2 text-body1 text-blue-600">
                        Click to replace image
                      </div>
                    </div>

                    <div className="absolute bottom-2 left-2 h-6 w-6">
                      <div className="absolute h-full w-full rounded-full bg-white/40 backdrop-blur-[2px]"></div>
                      <EditIcon className="absolute m-[3px] h-[18px] w-[18px] group-hover:fill-blue-600" />
                    </div>
                  </div>
                )}
              </>
            )}
            {isLoading && (
              <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-center">
                <Spinner />
                <Text variant="body2">Uploading image</Text>
              </div>
            )}
            {!!error && !isLoading && (
              <div
                className={cx(
                  "absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-1 rounded-lg p-2 text-center text-red"
                )}
              >
                {imageHeight > SMALL_IMAGE_THRESHOLD &&
                  imageWidth > SMALL_IMAGE_THRESHOLD && (
                    <WarningIcon className="h-4 w-4 fill-red" />
                  )}
                <Text variant="body2">{error}</Text>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
